import { useEffect, useState } from 'react';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import { Form, FormikProvider, useFormik } from 'formik';
import { toSnakeCase } from 'utils';
import { useCompany, useCompanyFunctions } from 'contexts/CompanyContext';
import { useHistory } from 'react-router-dom';
import BankFields, { bankFieldsInitialValues, bankValidationSchema } from 'components/BankFields';
import DefaultButton from 'components/DefaultButton';
import MaiaLogo from 'components/MaiaLogo';
import MaiaText from 'components/MaiaText';
import RegisterLayout from 'pages/RegisterLayout';
import routes from 'config/routes';
import sideImage from '_assets/register/credit_card.svg';
import useStyles from './styles';

const RegisterBank = () => {
  const classes = useStyles();
  const history = useHistory();

  const { currentCompany } = useCompany();
  const { editCompany } = useCompanyFunctions();

  const [errorDoc, setErrorDoc] = useState( { onError: false, onWarning: false, message: ''});

  const [document, setDocument] = useState({
    file: null,
    url: currentCompany?.bankURL ?? null,
  });

  function handleDocumentChange(file, url) {
    if (file?.type.endsWith('pdf')) {
      setErrorDoc( { onError: false, onWarning: false, message:null });
      setDocument({ file, url });
    } else if (document.file?.type.endsWith('pdf')) {
      setErrorDoc( { onErrror: false, onWarning: true, message: 'Le fichier doit être au format PDF.' });
    } else {
      setErrorDoc({ onError: true, onWarning: false, message: 'Le fichier doit être au format PDF.' });
    }
  }

  const formik = useFormik({
    validationSchema: bankValidationSchema,
    initialValues: bankFieldsInitialValues(currentCompany),
    onSubmit: async (values) => {
      if (!currentCompany) return;
      if (!document.file || errorDoc.onError) return;
      const documents = [];
      if (document?.file) {
        let formData = new FormData();
        formData.append('file', document?.file);
        documents.push({
          data: formData,
          name: 'bank',
          url: document?.url,
        });
      }

      const { edited } = await editCompany(currentCompany, toSnakeCase(values), documents);
      if (edited) history.push(routes.home.root);
    },
  });

  useEffect(() => {
    if(formik.isSubmitting){
      if (!document.file || errorDoc.onError) {
        setErrorDoc( {onError: true, onWarning: false, message: 'Un document bancaire valide est nécessaire.'});
      }
    }
    // eslint-disable-next-line
  }, [formik.isSubmitting] );

  function handleCancel() {
    if (formik.isSubmitting) return;
    history.push(routes.home.root);
  }

  const SideComponent = () => (
    <Grid container direction='column' style={{ height: 'auto' }}>
      <Grid container item justifyContent='center'>
        <MaiaLogo />
      </Grid>

      <Grid container item justifyContent='center' style={{ marginTop: '8vh' }}>
        <img src={sideImage} alt='Credit card' width={180} />
      </Grid>

      <Grid container item justifyContent='center' style={{ marginTop: '3vh' }}>
        <Typography className={classes.text} align='center' variant='h3'>
          Pas d’inquiétude l’utilisation de <MaiaText /> est gratuite.
        </Typography>
        <Typography className={classes.text} align='center' variant='h3'>
          Vos coordonnées bancaires nous servent à vous payer !
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <RegisterLayout SideComponent={<SideComponent />}>
      <Grid container direction='column' style={{ height: 'auto' }}>
        <Grid className={classes.header} container item justifyContent='center'>
          <Grid className={classes.title} container item xs={12} md={9} justifyContent='center'>
            <Typography variant='h1'>Vos coordonnées bancaires</Typography>
          </Grid>

          <Grid className={classes.title} container item xs={12} md={9} justifyContent='center'>
            <Typography align='center' variant='h2'>
              Les coordonées bancaires sont importantes si vous voulez que l'on vous paye.
            </Typography>
          </Grid>
        </Grid>

        <Grid container item justifyContent='center' style={{ flexGrow: 1 }}>
          <FormikProvider value={formik}>
            <Form className={classes.form}>
              <Grid container justifyContent='center' style={{ height: '100%' }}>
                <Grid container item xs={12} md={10} alignItems='center' justifyContent='center' direction='column' spacing={3} style={{ flexGrow: 1 }}>
                  <Grid container item>
                    <BankFields document={document} isEditing onDocumentChange={handleDocumentChange} />
                  </Grid>
                </Grid>

                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <div>
                    { (errorDoc.onError || errorDoc.onWarning) &&
                    <Typography color='error'>
                      {errorDoc.message}
                    </Typography>
                    }
                  </div>
                </div>

                <Grid container item xs={12} justifyContent='center'>
                  <Grid container item xs={12} alignItems='flex-end' justifyContent='center' style={{ marginBottom: '16px' }}>
                    <DefaultButton cancel onClick={handleCancel}>
                      Annuler
                    </DefaultButton>
                    <DefaultButton
                      color='secondary'
                      disabled={formik.isSubmitting}
                      type='submit'
                      variant='contained'
                    >
                      {formik.isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
                    </DefaultButton>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </Grid>
      </Grid>
    </RegisterLayout>
  );
};

export default RegisterBank;
